import * as React from "react"
import { cn } from "@lib/cn"
import { Label } from "./Label"

/**
 * Field — pairs a Label + control + description + error message with
 * automatic ARIA wiring. Generates a unique id, links `aria-describedby`
 * to the description and error, and flips `aria-invalid` when `error` is set.
 *
 * Usage:
 *   <Field label="Email" required description="We'll never share" error={errors.email}>
 *     <Input type="email" value={data.email} onChange={...} />
 *   </Field>
 */
export interface FieldProps {
  id?: string
  label?: React.ReactNode
  description?: React.ReactNode
  error?: React.ReactNode
  required?: boolean
  layout?: "stacked" | "inline"
  className?: string
  children: React.ReactElement
}

export function Field({
  id: idProp,
  label,
  description,
  error,
  required,
  layout = "stacked",
  className,
  children,
}: FieldProps) {
  const reactId = React.useId()
  const id = idProp ?? reactId
  const descId = description ? `${id}-desc` : undefined
  const errId = error ? `${id}-err` : undefined
  const describedBy = [descId, errId].filter(Boolean).join(" ") || undefined

  const child = React.cloneElement(children, {
    id,
    "aria-describedby": describedBy,
    "aria-invalid": error ? true : undefined,
    required,
    ...(children.props as Record<string, unknown>),
  } as Record<string, unknown>)

  return (
    <div
      className={cn(
        layout === "inline" ? "flex items-center gap-3" : "flex flex-col gap-1.5",
        className,
      )}
    >
      {label && (
        <Label htmlFor={id} required={required} className={layout === "inline" ? "min-w-28" : undefined}>
          {label}
        </Label>
      )}
      <div className={layout === "inline" ? "flex-1" : undefined}>
        {child}
        {description && !error && (
          <p id={descId} className="mt-1 text-mini text-cloverhound-gray-700 dark:text-cloverhound-gray-400">
            {description}
          </p>
        )}
        {error && (
          <p id={errId} className="mt-1 text-mini text-cloverhound-red-500">
            {error}
          </p>
        )}
      </div>
    </div>
  )
}
